// SPDX-License-Identifier: AGPL-3.0-or-later

import { useDeferredValue, useMemo } from 'react';
import type { Questionnaire } from '@intakeform/fhir';
import type { DraftAnswers } from '@intakeform/ui';
import {
  partitionFindings,
  validate,
  type Finding,
  type PartitionedFindings,
} from '@intakeform/validation';
import { buildQuestionnaireResponse } from '../stores/intake.js';

export interface LiveValidationResult {
  /** Per-field structural findings, safe to surface inline (filtered for `linkId`). */
  structural: Finding[];
  /** Form-level structural rollups (no `linkId`), e.g. `incomplete`. Surface as a banner. */
  formLevel: Finding[];
  /**
   * Findings the server may emit but the client deliberately does not preview.
   * Included for parity / debugging — the intake page does not render these
   * client-side; they only appear in the post-submit summary.
   */
  clinical: Finding[];
  /** True iff there are zero `error`-severity structural findings (form-level included). */
  ok: boolean;
}

const EMPTY_RESULT: LiveValidationResult = {
  structural: [],
  formLevel: [],
  clinical: [],
  ok: true,
};

/**
 * Runs the shared validation engine client-side over the current draft
 * and partitions the result into structural (patient-facing) and
 * clinical (server-only) buckets.
 *
 * Why a hook and not a plain call: the validation engine is fast but
 * non-trivial (multiple passes over every active item). Wrapping in
 * `useMemo` over the deferred answers means we don't re-run validation
 * on every keystroke — React batches the work behind a low-priority
 * update so input latency stays sharp.
 *
 * Note on the `clinical` bucket: the client *can* compute most clinical
 * findings (the engine is pure), but the partition exists so callers
 * have a single place to decide policy ("don't show these to the
 * patient"). Server-only async findings won't appear here because the
 * orchestrator's async results aren't passed in.
 */
export function useLiveValidation(args: {
  questionnaire: Questionnaire | undefined;
  answers: DraftAnswers;
  now?: Date;
  defaultFreshnessDays?: number;
}): LiveValidationResult {
  const { questionnaire, defaultFreshnessDays } = args;
  // useDeferredValue lets React drop intermediate validation runs when
  // the patient is typing fast. The first paint after a keystroke uses
  // the previous answers; the validation rerun lands a frame later.
  const deferredAnswers = useDeferredValue(args.answers);
  const now = args.now ?? new Date();

  return useMemo<LiveValidationResult>(() => {
    if (questionnaire === undefined) return EMPTY_RESULT;
    const response = buildQuestionnaireResponse({
      questionnaireRef: `Questionnaire/${questionnaire.id ?? 'draft'}|${questionnaire.version ?? '1'}`,
      status: 'in-progress',
      answers: deferredAnswers,
      questionnaire,
    });
    const result = validate({
      questionnaire,
      response,
      now,
      defaultFreshnessDays,
    });
    const partitioned: PartitionedFindings = partitionFindings(result.findings);
    const ok = ![...partitioned.structural, ...partitioned.formLevel].some(
      (f) => f.severity === 'error',
    );
    return {
      structural: partitioned.structural,
      formLevel: partitioned.formLevel,
      clinical: partitioned.clinical,
      ok,
    };
    // `now` is intentionally NOT in the deps — re-rendering on every
    // clock tick would defeat the deferred-value optimisation. Freshness
    // re-evaluates when answers change, which is the only thing that
    // can move a field's answeredAt. (No exhaustive-deps lint
    // configured at the workspace level, so no disable directive
    // needed.)
  }, [questionnaire, deferredAnswers, defaultFreshnessDays]);
}
